import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, LoadingSpinner, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  insurerProductsService,
  livePolicyText,
  STATUS_LABELS,
  type ProductStatus,
} from '@/services/insurerProducts';

/**
 * #398: activate / deactivate / discontinue one product.
 *
 * Going live is immediate (no approval step). Taking a product off sale needs
 * a reason and, when issued policies use it, a confirmation that they stay
 * in force. Discontinued is final for the insurer.
 */

const ACTION_TEXT: Record<ProductStatus, { title: string; button: string }> = {
  active: { title: 'Activate product', button: 'Activate' },
  inactive: { title: 'Deactivate product', button: 'Deactivate' },
  discontinued: { title: 'Discontinue product', button: 'Discontinue' },
  draft: { title: 'Move back to draft', button: 'Move to draft' },
};

export function ProductStatusDialog({
  productId,
  productName,
  target,
  onClose,
}: {
  productId: string;
  productName: string;
  target: ProductStatus | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);
  const offSale = target === 'inactive' || target === 'discontinued';

  useEffect(() => {
    setReason('');
    setConfirmed(false);
    setMissing([]);
  }, [target]);

  const { data: detail, isLoading } = useQuery({
    queryKey: ['insurer-product', productId],
    queryFn: () => insurerProductsService.get(productId),
    enabled: !!target,
  });
  const live = detail?.live_policy_count;
  const needsConfirm = offSale && (!live || live.precision === 'unknown' || (live.count ?? 0) > 0);

  const mutation = useMutation({
    mutationFn: () =>
      insurerProductsService.setStatus(productId, {
        status: target as ProductStatus,
        reason,
        confirm_live_policies: confirmed,
      }),
    onSuccess: () => {
      toast.success(
        `${productName} is now ${STATUS_LABELS[target as ProductStatus].toLowerCase()}.`
      );
      queryClient.invalidateQueries({ queryKey: ['insurance-policies'] });
      queryClient.invalidateQueries({ queryKey: ['insurer-product', productId] });
      queryClient.invalidateQueries({ queryKey: ['insurer-product-versions', productId] });
      onClose();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: { missing?: string[] } } } }).response
        ?.data;
      if (data?.error?.missing?.length) {
        setMissing(data.error.missing);
        return;
      }
      toast.error(getErrorMessage(err));
    },
  });

  if (!target) return null;
  const text = ACTION_TEXT[target];
  const blocked = (offSale && !reason.trim()) || (needsConfirm && !confirmed);

  return (
    <Modal isOpen onClose={onClose} title={`${text.title}: ${productName}`}>
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
        {target === 'active' && (
          <p>The product goes on sale for your connected brokers immediately.</p>
        )}
        {offSale && (
          <>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <p>
                {livePolicyText(live)} They stay in force; renewals, endorsements and claims are
                unaffected. New quotes and sales stop immediately.
              </p>
            )}
            {target === 'discontinued' && (
              <p className="font-medium text-red-700 dark:text-red-400">
                Discontinuing is final: only your country admin can bring the product back.
              </p>
            )}
            <label
              htmlFor="status-reason"
              className="block font-medium text-gray-700 dark:text-gray-300"
            >
              Reason (required)
            </label>
            <textarea
              id="status-reason"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
            {needsConfirm && (
              <label className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                />
                I understand issued policies stay in force and new sales stop now.
              </label>
            )}
          </>
        )}
        {missing.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            <p className="font-medium">Complete these before activating:</p>
            <ul className="mt-1 list-disc pl-5">
              {missing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={offSale ? 'danger' : 'primary'}
          onClick={() => mutation.mutate()}
          isLoading={mutation.isPending}
          disabled={blocked}
        >
          {text.button}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
